/**
 * Share Token Utilities
 * Generate and validate unique tokens for public sharing
 */

/**
 * Generate a cryptographically random token for public sharing
 * Format: 16 character alphanumeric string (base36)
 */
export function generateShareToken(): string {
  const array = new Uint8Array(16);
  crypto.getRandomValues(array);

  // Convert to base36 for URL-safe characters (0-9, a-z)
  return Array.from(array, byte => byte.toString(36))
    .join('')
    .substring(0, 16)
    .toUpperCase();
}

/**
 * Validate share token format
 */
export function isValidShareToken(token: string): boolean {
  // Token should be 16 alphanumeric characters
  return /^[A-Z0-9]{16}$/.test(token);
}

/**
 * Generate a shareable URL for a quote
 */
export function getPublicQuoteUrl(token: string, baseUrl: string = window.location.origin): string {
  return `${baseUrl}/q/${token}`;
}

/**
 * Format phone number for WhatsApp wa.me link
 * Removes all non-digit characters and ensures international format
 */
export function formatPhoneNumberForWhatsApp(phone: string, countryCode: string = '254'): string {
  // Remove all non-digit characters
  const cleaned = phone.replace(/\D/g, '');

  // If number starts with 0, replace with country code
  if (cleaned.startsWith('0')) {
    return countryCode + cleaned.substring(1);
  }

  // If number doesn't start with +, add country code if it looks like a local number
  if (!cleaned.startsWith('+')) {
    // If it's a 9-10 digit number (likely local), prepend country code
    if (cleaned.length <= 10) {
      return countryCode + cleaned;
    }
  }

  // Remove + if present
  return cleaned.replace(/^\+/, '');
}

/**
 * Create WhatsApp share link
 */
export function createWhatsAppLink(phoneNumber: string, message: string): string {
  const formattedPhone = formatPhoneNumberForWhatsApp(phoneNumber);
  const encodedMessage = encodeURIComponent(message);
  return `https://wa.me/${formattedPhone}?text=${encodedMessage}`;
}

/**
 * Message templates for WhatsApp quote sharing
 * Randomized to provide variety for clients
 */
const QUOTE_MESSAGE_TEMPLATES = [
  // Template 1: Professional & Direct
  (
    clientName: string,
    quoteNumber: string,
    amount: string,
    publicUrl: string
  ) => `Dear ${clientName},

I trust this message finds you well. I'm pleased to share Quote #${quoteNumber} for your review.

*Quote Details:*
Total Amount: ${amount}

Please review the detailed quotation at the link below:
${publicUrl}

The quote includes all discussed deliverables, timelines, and terms. Should you have any questions or require clarification on any aspect, please don't hesitate to reach out.

I look forward to your feedback.`,

  // Template 2: Warm & Detailed
  (
    clientName: string,
    quoteNumber: string,
    amount: string,
    publicUrl: string
  ) => `Hi ${clientName},

Thank you for the opportunity to work together. I've prepared Quote #${quoteNumber} based on our recent discussion.

*Quote Summary:*
Quote Reference: #${quoteNumber}
Total Investment: ${amount}

You can view the complete quote with full breakdown here:
${publicUrl}

This document outlines the scope of work, pricing, and project timeline. Please take your time to review it, and let me know if you'd like to discuss any adjustments.

Excited to move forward!`,

  // Template 3: Business-Focused
  (
    clientName: string,
    quoteNumber: string,
    amount: string,
    publicUrl: string
  ) => `Hello ${clientName},

Following our conversation, I'm submitting Quote #${quoteNumber} for your consideration.

*Proposal Overview:*
Quote Number: #${quoteNumber}
Total Value: ${amount}

Access the full quotation via this secure link:
${publicUrl}

The quote details the services, deliverables, and pricing structure as discussed. I'm confident this proposal meets your requirements and budget.

Available for a call if you need any clarifications.`,

  // Template 4: Concise & Professional
  (
    clientName: string,
    quoteNumber: string,
    amount: string,
    publicUrl: string
  ) => `${clientName},

Your quote is ready for review.

*Quote #${quoteNumber}*
Total: ${amount}

View/download: ${publicUrl}

All terms and conditions are included in the document. Please confirm once you've reviewed it, or let me know if you have questions.`,

  // Template 5: Enthusiastic & Service-Oriented
  (
    clientName: string,
    quoteNumber: string,
    amount: string,
    publicUrl: string
  ) => `Hi ${clientName},

Great connecting with you! I've just sent over Quote #${quoteNumber} for the project we discussed.

*Quick Summary:*
Quote ID: #${quoteNumber}
Estimated Cost: ${amount}

Click below to view the detailed quote:
${publicUrl}

I've included a comprehensive breakdown of all services and costs. Feel free to reach out if anything needs clarification - I'm happy to walk you through it.

Looking forward to working together!`
];

/**
 * Create quote WhatsApp message with random professional template
 */
export function createQuoteWhatsAppMessage(
  quoteNumber: string,
  clientName: string,
  amount: string,
  publicUrl: string
): string {
  // Select a random template
  const randomIndex = Math.floor(Math.random() * QUOTE_MESSAGE_TEMPLATES.length);
  const template = QUOTE_MESSAGE_TEMPLATES[randomIndex];
  if (!template) {
    throw new Error('Failed to select message template');
  }
  return template(clientName, quoteNumber, amount, publicUrl);
}
